import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import FlowChips from "../automations/FlowChips";
import ProductChip from "../automations/ProductChip";
import TriggerSummary from "../automations/TriggerSummary";

const id = "modal-start-from-template";
const TABS = ["msb", "mine"];

const TemplateCard = ({ template, product, options, selected, onToggle }) => {
  const { t, i18n } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.templates.${key}`, params);
  const saved = template.createdAt
    ? new Date(template.createdAt).toLocaleDateString(i18n.language, {
        month: "short",
        day: "numeric",
      })
    : null;
  const withName = {
    ...template,
    product,
    trigger: { ...template.trigger, name: template.triggerName },
  };
  return (
    <label className={`ea-template${selected ? " ea-template--selected" : ""}`}>
      <input
        type="checkbox"
        className="sr-only"
        checked={selected}
        onChange={onToggle}
      />
      <i className="ea-template__check" aria-hidden="true" />
      <strong>{template.name}</strong>
      <span className="ea-template__description">{template.description}</span>
      {template.createdByName ? (
        <small>
          {tr("savedBy", { name: template.createdByName, date: saved })}
        </small>
      ) : null}
      <TriggerSummary automation={withName} options={options} short />
      <FlowChips automation={template} />
    </label>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  product: PropTypes.string.isRequired,
  options: PropTypes.object,
  selected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Starter automations: MSB's and the ones the account saved. Every one picked is added as a draft.
const StartFromTemplate = ({
  templates,
  mine,
  teamName,
  triggers,
  options,
  product,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.templates.${key}`, params);
  const [tab, setTab] = useState("msb");
  const [picked, setPicked] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const lists = { msb: templates, mine };
  const nameOf = (template) => ({
    ...template,
    triggerName:
      triggers.find((trigger) => trigger.key === template.trigger.key)?.name ??
      template.trigger.key,
  });
  const toggle = (key) =>
    setPicked((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );

  return (
    <form
      action="/actions/automations/from-template"
      method="POST"
      data-noreset
    >
      <input type="hidden" name="product" value={product} />
      {picked.map((key) => (
        <input key={key} type="hidden" name="keys" value={key} />
      ))}
      <Modal
        name={id}
        key={id}
        className="ea-modal ea-modal--templates"
        customTitle={
          <h2 className="ea-modal__title">
            {confirming
              ? tr("confirmAll.title", { count: lists[tab].length })
              : tr("title")}
            <ProductChip product={product} />
          </h2>
        }
      >
        {confirming ? (
          <div className="body">
            <p className="ea-modal__lead">
              {tr("confirmAll.body", { count: lists[tab].length })}
            </p>
          </div>
        ) : (
          <div className="body">
            <p className="ea-modal__lead">{tr("subtitle")}</p>
            <div className="ea-tabs" role="tablist">
              {TABS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                >
                  {tr(`tabs.${key}`, { count: lists[key].length })}
                </button>
              ))}
            </div>
            {lists[tab].length === 0 ? (
              <p className="ea-modal__lead">
                {tr(tab === "mine" ? "noneMine" : "none")}
              </p>
            ) : (
              <div className="ea-templates">
                {lists[tab].map((template) => (
                  <TemplateCard
                    key={template.key}
                    template={nameOf(template)}
                    product={product}
                    options={options}
                    selected={picked.includes(template.key)}
                    onToggle={() => toggle(template.key)}
                  />
                ))}
              </div>
            )}
            <p className="ea-modal__note">
              {tr("saveHint", { team: teamName })}
            </p>
          </div>
        )}
        <footer className="foot">
          {confirming ? (
            <button
              type="button"
              data-button-type="secondary"
              data-action="cancel"
              onClick={() => setConfirming(false)}
            >
              {tr("cancel")}
            </button>
          ) : (
            // biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles
            <a //NOSONAR
              href="/actions/refresh?modal=&product="
              data-button-type="secondary"
              data-action="cancel"
              role="button"
            >
              {tr("cancel")}
            </a>
          )}
          {confirming ? (
            <button
              type="submit"
              name="all"
              value={tab}
              formNoValidate
              data-button-type="primary"
            >
              {tr("confirmAll.confirm", { count: lists[tab].length })}
            </button>
          ) : (
            <>
              <button
                type="submit"
                data-button-type="primary"
                disabled={picked.length === 0}
              >
                {picked.length > 0
                  ? tr("add", { count: picked.length })
                  : tr("addNone")}
              </button>
              {lists[tab].length > 1 ? (
                <button
                  type="button"
                  data-button-type="secondary"
                  onClick={() => setConfirming(true)}
                >
                  {tr("addAll", { count: lists[tab].length })}
                </button>
              ) : null}
            </>
          )}
        </footer>
      </Modal>
    </form>
  );
};

StartFromTemplate.propTypes = {
  templates: PropTypes.array.isRequired,
  mine: PropTypes.array.isRequired,
  teamName: PropTypes.string,
  triggers: PropTypes.array.isRequired,
  options: PropTypes.object,
  product: PropTypes.string.isRequired,
};

export default StartFromTemplate;
